import React from 'react';
import TranslationNamespaces from '../../locales/TranslationNamespaces';
import { useTranslation } from 'react-i18next';
import CenteredQButton from '../Shared/CenteredQButton';
import FlexContainer from '../Shared/FlexContainers/FlexContainer';
import HeaderWithDescription from '../Shared/HeaderWithDescription';
import MessageBox from '../Shared/MessageBoxes/MessageBox';
import { useCache } from '../../state/CachingContext';
import { QConfirm } from '@kubiq-base/core';
import { useApi } from '../../state/ApiContext';
import { useNavigate } from 'react-router-dom';

interface Props {
  recoveryMode: 'PW' | '2FA' | 'DEL' | 'NotVerified' | 'NoRecovery';
}

const Confirm = ({ recoveryMode }: Props): JSX.Element => {
  const namespace = TranslationNamespaces.recovery;
  const { t } = useTranslation(namespace);
  const { postRequest } = useApi();
  const { getCache } = useCache();
  const navigate = useNavigate();

  const email = getCache('email');
  const verifiedPhone = getCache('verifiedPhone');

  const [localMode, setLocalMode] = React.useState(recoveryMode);

  const [openConfirm, setOpenConfirm] = React.useState(false);
  const [openConfirmDelete, setOpenConfirmDelete] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const request2FAReset = async (): Promise<void> => {
    setIsSubmitting(true);
    await postRequest('TwoFactorAuthentication/ResetAuthenticator', {})
      .then(() => navigate('/Recovery/CheckEmail'))
      .catch(() => setIsSubmitting(false));
  };

  const requestDeleteAccount = async (): Promise<void> => {
    setIsSubmitting(true);
    await postRequest('User/RequestAccountRemoval', { email: email })
      .then(() => navigate('/Recovery/CheckEmail'))
      .catch(() => setIsSubmitting(false));
  };

  React.useEffect(() => {
    setLocalMode(recoveryMode);
  }, [recoveryMode]);

  return (
    <>
      {(localMode === 'NotVerified' && !verifiedPhone) || localMode === 'NoRecovery' ? (
        <>
          {localMode === 'NotVerified' && (
            <HeaderWithDescription
              translationNamespace={namespace}
              headerSuffix='NotVerified'
              description='descriptionNotVerified'
            />
          )}
          {localMode === 'NoRecovery' && (
            <HeaderWithDescription
              translationNamespace={namespace}
              headerSuffix='NoRecovery'
              description='descriptionNoRecovery'
            />
          )}

          <FlexContainer>
            <CenteredQButton onClick={() => setOpenConfirmDelete(true)} color='secondary' fullWidth>
              {t('deleteAccount')}
            </CenteredQButton>

            <CenteredQButton to='/' variant='text' disabled={isSubmitting}>
              {t('cancel')}
            </CenteredQButton>
          </FlexContainer>

          <QConfirm
            open={openConfirmDelete}
            onClose={() => setOpenConfirmDelete(false)}
            onConfirm={() => navigate('/Recovery/Account')}
            title={t('youSure')}
            message={t('startDelete')}
          />
        </>
      ) : (
        <>
          {(verifiedPhone || localMode === 'DEL') && (
            <>
              <HeaderWithDescription
                translationNamespace={namespace}
                headerSuffix={`${localMode}Recovery`}
                description='descriptionConfirmEmail'
              >
                <MessageBox text={email} emailBox />
              </HeaderWithDescription>

              <FlexContainer>
                {localMode === 'PW' && (
                  <CenteredQButton to='/Recovery/Authenticate' color='secondary' fullWidth>
                    {t('confirm')}
                  </CenteredQButton>
                )}

                {localMode === '2FA' && (
                  <CenteredQButton
                    onClick={() => setOpenConfirm(true)}
                    color='secondary'
                    fullWidth
                    isLoading={isSubmitting}
                  >
                    {t('confirm')}
                  </CenteredQButton>
                )}

                {localMode === 'DEL' && (
                  <CenteredQButton
                    onClick={() => requestDeleteAccount()}
                    color='secondary'
                    fullWidth
                    isLoading={isSubmitting}
                  >
                    {t('confirm')}
                  </CenteredQButton>
                )}

                <CenteredQButton to={localMode === '2FA' ? '/Auth/2FA' : '/'} variant='text' disabled={isSubmitting}>
                  {t('cancel')}
                </CenteredQButton>
              </FlexContainer>

              <QConfirm
                open={openConfirm}
                onClose={() => setOpenConfirm(false)}
                onConfirm={() => request2FAReset()}
                title={t('youSure')}
                message={t('startReset')}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default Confirm;
